import * as React from "react";
import { FileError, useDropzone } from "react-dropzone";

import { removeMedia, uploadMedia } from "@/api/client";
import {
  MEDIA_ACCEPT,
  MEDIA_MAX_BYTES,
  MEDIA_PICK_MAX_BYTES,
  MEDIA_TYPES,
  MediaKind,
  SOURCE_KIND,
} from "@/api/types";
import { cn } from "@/lib/utils";

import { BANNER_GRADIENT } from "../booking/public/PublicChrome";
import { ImageCropper } from "./ImageCropper";
import { frameAspect } from "./cropGeometry";
import { cropSupported } from "./imageExport";

/**
 * Upload, reframe or remove the two images a host publishes.
 *
 * <p>Three ways in, because people reach for different ones: click the tile, drop a file anywhere on
 * the field, or paste from the clipboard. Every path lands in the same cropper, so the framing step
 * is never skipped — that step is what makes the result reliable rather than lucky.</p>
 *
 * <p>A fourth way in is <b>Adjust</b>, which needs no file at all. Every upload stores a bounded copy
 * of the original alongside the framed one, so changing your mind about the framing later costs a
 * click rather than a hunt through Downloads for a picture you chose in March.</p>
 *
 * <p>The drop target is the whole field rather than the tile, which for an avatar is a 96px circle —
 * a target small enough that missing it is the expected outcome rather than the unlucky one. Dropping
 * outside a target and watching the browser navigate away to the image is the failure that costs a
 * host their unsaved form.</p>
 *
 * <p>These save on their own, not with the profile form's button. By the time the host sees the
 * result it is already in storage, so a Save that could still be cancelled would be a lie.</p>
 */

const LABELS: Record<MediaKind, { title: string; empty: string }> = {
  AVATAR: { title: "Profile picture", empty: "Drop a photo, or click to choose" },
  BANNER: { title: "Branding banner", empty: "Drop an image, or click to choose" },
};

/** Rewritten in our own words; the library's defaults name codes and byte counts. */
function messageFor(error: FileError): string {
  if (error.code === "file-too-large") {
    return `That file is larger than ${MEDIA_PICK_MAX_BYTES / (1024 * 1024)} MB.`;
  }
  if (error.code === "file-invalid-type") return "Use a JPEG, PNG, WebP or HEIC.";
  return error.message;
}

/** Whether a failed upload failed because the host stopped it. */
function cancelled(failure: unknown): boolean {
  return failure instanceof DOMException && failure.name === "AbortError";
}

export function ProfileImagesFields({
  avatarUrl,
  bannerUrl,
  avatarSourceUrl,
  bannerSourceUrl,
  hostName,
  onChanged,
  disabled,
}: {
  avatarUrl: string | null;
  bannerUrl: string | null;
  avatarSourceUrl?: string | null;
  bannerSourceUrl?: string | null;
  hostName: string;
  onChanged: (kind: MediaKind, url: string | null) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-4">
      <ImageField
        kind="AVATAR"
        url={avatarUrl}
        sourceUrl={avatarSourceUrl}
        otherUrl={bannerUrl}
        hostName={hostName}
        onChanged={onChanged}
        disabled={disabled}
      />
      <ImageField
        kind="BANNER"
        url={bannerUrl}
        sourceUrl={bannerSourceUrl}
        otherUrl={avatarUrl}
        hostName={hostName}
        onChanged={onChanged}
        disabled={disabled}
      />
    </div>
  );
}

function ImageField({
  kind,
  url,
  sourceUrl,
  otherUrl,
  hostName,
  onChanged,
  disabled,
}: {
  kind: MediaKind;
  url: string | null;
  sourceUrl?: string | null;
  /** The host's OTHER image, so the cropper's preview shows the real pairing. */
  otherUrl: string | null;
  hostName: string;
  onChanged: (kind: MediaKind, url: string | null) => void;
  disabled?: boolean;
}) {
  // `reframe` distinguishes a fresh pick from Adjust. The difference matters at save time: reframing
  // starts from the stored original, so re-uploading it would spend a round trip re-encoding a file
  // we already have, and cost a generation of quality to do it.
  const [picked, setPicked] = React.useState<{ file: File; reframe: boolean } | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = React.useState(false);
  /** Present only while bytes are in flight for the picture the host is waiting on. */
  const [upload, setUpload] = React.useState<{ fraction: number; cancel: () => void } | null>(null);
  // Held here rather than lifted, because nothing outside this field reads it: the profile form
  // cares which picture is published, not which original sits behind it.
  const [source, setSource] = React.useState<string | null>(sourceUrl ?? null);
  React.useEffect(() => setSource(sourceUrl ?? null), [sourceUrl]);

  const round = kind === "AVATAR";
  const { title, empty } = LABELS[kind];
  const maxMb = MEDIA_MAX_BYTES[kind] / (1024 * 1024);

  function accept(file: File | undefined | null) {
    if (!file) return;
    setError(null);
    if (!cropSupported()) {
      // No canvas, so nothing can be converted on the way through. Formats the server serves can
      // still go up as picked and let its limit speak; the rest genuinely cannot be helped here.
      if (!MEDIA_TYPES.includes(file.type)) {
        setError("This browser can't prepare that format. Use a JPEG, PNG or WebP.");
        return;
      }
      void send(file);
      return;
    }
    setPicked({ file, reframe: false });
  }

  // One drop target for the whole field, and the library's job rather than ours: it settles the
  // dragleave-over-a-child flicker, the file dialog's cancel event, and paste, each of which is a
  // small pile of browser differences we would otherwise be maintaining by hand.
  const { getRootProps, getInputProps, isDragActive, isDragReject, open } = useDropzone({
    accept: MEDIA_ACCEPT,
    maxSize: MEDIA_PICK_MAX_BYTES,
    maxFiles: 1,
    multiple: false,
    disabled: disabled || busy,
    // The field holds Adjust and Remove, so a click anywhere inside it must not mean "choose a
    // file". The tile and the Upload button call open() themselves.
    noClick: true,
    noKeyboard: true,
    getErrorMessage: messageFor,
    onDrop: (accepted, rejections) => {
      if (rejections.length > 0) {
        setError(rejections[0].errors[0]?.message ?? "That file can't be used.");
        return;
      }
      accept(accepted[0]);
    },
  });

  /** Reopen the cropper on the kept original, with no file picker in sight. */
  async function onAdjust() {
    if (!source) return;
    setError(null);
    setBusy(true);
    try {
      // Same origin as the app: /media/* is a behaviour on the SPA's own distribution, so this is a
      // plain fetch rather than anything that needs CORS arranged for it.
      const res = await fetch(source);
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      setPicked({ file: new File([blob], `${kind.toLowerCase()}.webp`, { type: blob.type }), reframe: true });
    } catch {
      setError("Couldn't open that for adjusting. Upload it again.");
    } finally {
      setBusy(false);
    }
  }

  async function send(file: File, sourceFile?: File) {
    setBusy(true);
    const controller = new AbortController();
    setUpload({ fraction: 0, cancel: () => controller.abort() });
    try {
      // Checked AFTER framing, since that is what determines the final size: an 12MB photo becomes
      // a couple of hundred kilobytes, so this almost never fires.
      if (file.size > MEDIA_MAX_BYTES[kind]) {
        setError(`That image is larger than ${maxMb} MB.`);
        return;
      }
      const saved = await uploadMedia(kind, file, {
        signal: controller.signal,
        onProgress: (fraction) => setUpload((u) => (u ? { ...u, fraction } : u)),
      });
      onChanged(kind, saved.url);

      // Best effort, and deliberately after the picture the host is waiting for. Losing the original
      // costs them the Adjust button next time; failing their upload over it would cost them the
      // upload. The wrong one to protect is obvious.
      //
      // No progress bar and no cancel for this one: it carries nothing the host is waiting to see,
      // so a second bar would be asking them to watch something that does not concern them.
      setUpload(null);
      if (sourceFile) {
        try {
          const kept = await uploadMedia(SOURCE_KIND[kind], sourceFile);
          setSource(kept.url);
        } catch {
          setSource(null);
        }
      }
    } catch (failure) {
      // A cancel is the host getting what they asked for, so it reports nothing.
      setError(cancelled(failure) ? null : "Couldn't upload that. Try again.");
    } finally {
      setUpload(null);
      setBusy(false);
    }
  }

  async function onRemove() {
    setError(null);
    setConfirmingRemove(false);
    setBusy(true);
    try {
      // The server drops the kept original with it: one call, and no way to leave an orphan behind
      // that a later Adjust would reopen onto a picture the host thought they had deleted.
      await removeMedia(kind);
      onChanged(kind, null);
      setSource(null);
    } catch {
      setError("Couldn't remove that. Try again.");
    } finally {
      setBusy(false);
    }
  }

  // The tile previews at the SAME shape as the booking page, so what a host approves here is what
  // they will see there.
  const tile = round
    ? { width: 96, height: 96 }
    : { width: 288, height: Math.round(288 / frameAspect("BANNER")) };

  return (
    <div className="space-y-2">
      <div className="font-comic-headline text-sm uppercase tracking-wide text-foreground">{title}</div>

      <div
        {...getRootProps({
          className: cn(
            "space-y-2 rounded-xl border border-transparent p-2 transition",
            // Only while a file is over it. A permanently outlined region would read as a control,
            // and there is nothing here to click that is not already a button.
            isDragActive && !isDragReject && "border-dashed border-brand-blue bg-brand-blue/5",
            isDragReject && "border-dashed border-destructive bg-destructive/5",
          ),
        })}
      >
        <input {...getInputProps()} />

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={disabled || busy}
            onClick={open}
            style={{ width: tile.width, height: tile.height }}
            className={cn(
              "relative shrink-0 overflow-hidden border transition",
              // bg-card once there is an image, because a fitted one is transparent where it does not
              // reach and the tile has to show the same thing the invitee page will.
              url ? "bg-card" : "border-dashed bg-muted/30",
              round ? "rounded-full" : "rounded-lg",
              isDragActive ? "border-brand-blue ring-2 ring-brand-blue" : "border-border",
              "hover:border-brand-blue disabled:opacity-60",
            )}
            aria-label={url ? `Replace your ${title.toLowerCase()}` : `Add your ${title.toLowerCase()}`}
          >
            {url ? (
              <img src={url} alt="" className="h-full w-full object-cover" />
            ) : round ? (
              <span className="grid h-full place-items-center px-2 text-center text-[11px] leading-tight text-muted-foreground">
                Add
              </span>
            ) : (
              // The real default, not a placeholder: an empty banner tile shows the gradient a host's
              // pages already use, so "what happens if I add nothing" is answered by looking.
              <span
                className="grid h-full place-items-center px-2 text-center text-[11px] font-medium leading-tight text-[#ffffffe6]"
                style={{ background: BANNER_GRADIENT }}
              >
                {empty}
              </span>
            )}
            {busy && !upload && (
              <span className="absolute inset-0 grid place-items-center bg-background/70 text-[11px]">
                Working…
              </span>
            )}
          </button>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={disabled || busy}
              onClick={open}
              className="rounded-lg border border-border px-2.5 py-1 text-sm hover:bg-muted/50 disabled:opacity-50"
            >
              {url ? "Replace" : "Upload"}
            </button>
            {url && source && (
              <button
                type="button"
                disabled={disabled || busy}
                onClick={onAdjust}
                className="rounded-lg border border-border px-2.5 py-1 text-sm hover:bg-muted/50 disabled:opacity-50"
              >
                Adjust
              </button>
            )}
            {url && (
              <button
                type="button"
                disabled={disabled || busy}
                onClick={() => setConfirmingRemove(true)}
                className="rounded-lg border border-border px-2.5 py-1 text-sm hover:bg-muted/50 disabled:opacity-50"
              >
                Remove
              </button>
            )}
          </div>
        </div>

        {upload && <UploadProgress fraction={upload.fraction} onCancel={upload.cancel} />}
      </div>

      {/* Removal used to happen on the click. It deletes the bytes AND the kept original, so
          there is nothing to undo, and it changes what strangers see on every public link at
          once — which is the part a host has no way to picture from this screen. The house
          pattern for that is an inline panel rather than a modal (see the availability editor). */}
      {confirmingRemove && (
        <div className="space-y-2 rounded-xl border border-border bg-muted/30 p-3">
          <p className="text-sm font-semibold text-foreground">
            Remove your {round ? "profile picture" : "banner"}?
          </p>
          <p className="text-xs text-muted-foreground">
            {round
              ? "Your booking links will show your initials instead. The picture and the original you framed it from are both deleted, so this cannot be undone."
              : "Your booking links will go back to their plain header. The banner and the original you framed it from are both deleted, so this cannot be undone."}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={onRemove}
              className="rounded-lg border border-destructive px-2.5 py-1 text-sm font-semibold text-destructive-strong hover:bg-destructive/10 disabled:opacity-50"
            >
              Remove
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirmingRemove(false)}
              className="rounded-lg border border-border px-2.5 py-1 text-sm hover:bg-muted/50 disabled:opacity-50"
            >
              Keep it
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-xs text-destructive-strong">{error}</p>}

      {picked && (
        <ImageCropper
          file={picked.file}
          kind={kind}
          hostName={hostName}
          otherAvatarUrl={round ? null : otherUrl}
          otherBannerUrl={round ? otherUrl : null}
          onCancel={() => setPicked(null)}
          onCropped={(cropped, sourceFile) => {
            const reframe = picked.reframe;
            setPicked(null);
            void send(cropped, reframe ? undefined : sourceFile);
          }}
        />
      )}
    </div>
  );
}

/**
 * How far the bytes have got, and a way to stop them.
 *
 * <p>Worth the room it takes only because the wait is real: a framed banner is a few hundred
 * kilobytes, but the connection it is going over may not be, and an upload with no visible progress
 * is indistinguishable from one that has failed. Cancel is the other half of that — knowing it is
 * stuck is no use without being able to do something about it.</p>
 */
function UploadProgress({ fraction, onCancel }: { fraction: number; onCancel: () => void }) {
  const percent = Math.round(Math.min(1, Math.max(0, fraction)) * 100);
  return (
    <div className="flex items-center gap-2">
      <div
        className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-label="Upload progress"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full rounded-full bg-brand-blue transition-[width] duration-150"
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="w-9 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
        {percent}%
      </span>
      <button
        type="button"
        onClick={onCancel}
        className="rounded-lg border border-border px-2 py-0.5 text-xs hover:bg-muted/50"
      >
        Cancel
      </button>
    </div>
  );
}
